// Historial de aceptación de Términos y Condiciones (cláusula 4.4).
// El cliente lo abre desde el submenú del área de usuario (header). Consulta el estado
// de la cuenta frente a la versión vigente y lista todas sus aceptaciones (versión, fecha, canal).
import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Table, Badge, Alert, Spinner } from 'react-bootstrap';
import useAuthHeaders from '../hooks/useAuthHeaders';

const API_URL = process.env.REACT_APP_API_URL;

const CHANNEL_LABELS = {
  Web: 'Web',
  EmailLink: 'Correo',
  Api: 'API',
};

const formatDate = (utc) => {
  if (!utc) return '—';
  // Los valores llegan en UTC; se muestran en hora local (es-MX).
  const iso = /[zZ]|[+-]\d{2}:\d{2}$/.test(utc) ? utc : `${utc}Z`;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return utc;
  return d.toLocaleString('es-MX', {
    year: 'numeric', month: 'long', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

const TermsHistoryModal = ({ show, onHide }) => {
  const { getToken } = useAuthHeaders();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState(null);   // { enabled, currentVersion, isCurrent, ... }
  const [history, setHistory] = useState([]);    // [{ version, acceptedAtUtc, channel }]

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await getToken();
      const headers = { Authorization: `Bearer ${token}` };

      const [statusRes, historyRes] = await Promise.all([
        fetch(`${API_URL}/terminos/estado`, { headers }),
        fetch(`${API_URL}/terminos/historial`, { headers }),
      ]);

      if (statusRes.ok) setStatus(await statusRes.json());
      if (historyRes.ok) {
        setHistory(await historyRes.json());
      } else {
        setError('No se pudo cargar el historial de aceptaciones.');
      }
    } catch {
      setError('Error de conexión al cargar el historial.');
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => {
    if (show) load();
  }, [show, load]);

  return (
    <Modal show={show} onHide={onHide} centered scrollable size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Mis Términos y Condiciones</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {status?.enabled && status.currentVersion && (
          <p className="mb-3">
            Versión vigente: <strong>{status.currentVersion}</strong>{' '}
            {status.isCurrent ? (
              <Badge bg="success">Al día</Badge>
            ) : (
              <Badge bg="warning" text="dark">Pendiente de aceptar</Badge>
            )}
          </p>
        )}

        {error && <Alert variant="danger">{error}</Alert>}

        {loading ? (
          <div className="text-center py-4">
            <Spinner animation="border" size="sm" className="me-2" />
            Cargando…
          </div>
        ) : history.length === 0 ? (
          !error && <p className="text-muted mb-0">Aún no has aceptado los Términos y Condiciones.</p>
        ) : (
          <Table striped bordered hover responsive size="sm" className="mb-0">
            <thead>
              <tr>
                <th>Versión</th>
                <th>Fecha de aceptación</th>
                <th>Canal</th>
              </tr>
            </thead>
            <tbody>
              {history.map((item, idx) => (
                <tr key={idx}>
                  <td>{item.version}</td>
                  <td>{formatDate(item.acceptedAtUtc)}</td>
                  <td>{CHANNEL_LABELS[item.channel] || item.channel}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default TermsHistoryModal;
